import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileArchive,
  FileText,
  Film,
  Folder,
  Image,
  Music,
  Star,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'

import { formatBytes } from '../../lib/format'
import { cx } from '../../ui'
import type { SharedAsset } from '../../portal/types'

interface Props {
  assets: SharedAsset[]
  index: number
  canSelect: boolean
  canDownload: boolean
  quotaReached: boolean
  onIndexChange: (index: number) => void
  onToggleSelect: (asset: SharedAsset) => void
  onDownload: (asset: SharedAsset) => void
  onClose: () => void
  /** Al llegar al final se pide otra página, para que el visor no tope. */
  onNeedMore: () => void
}

const KIND_ICON: Record<SharedAsset['kind'], ComponentType<LucideProps>> = {
  image: Image,
  video: Film,
  audio: Music,
  document: FileText,
  archive: FileArchive,
  other: Folder,
}

/**
 * Visor a pantalla completa.
 *
 * Es donde el cliente DECIDE. La retícula recorta las fotos en cuadrado, así
 * que ahí no puede juzgar el encuadre — que es justo lo que se le pide en un
 * proofing. Aquí la ve entera (`object-contain`) y elige con criterio.
 *
 * También es lo que impide que descargue sin querer: en la retícula el toque
 * abre, no descarga. En móvil, con 400 fotos y el pulgar, tocar por error es
 * constante y cada descarga accidental son datos del cliente.
 */
export function Lightbox({
  assets,
  index,
  canSelect,
  canDownload,
  quotaReached,
  onIndexChange,
  onToggleSelect,
  onDownload,
  onClose,
  onNeedMore,
}: Props) {
  const asset = assets[index]
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const [loaded, setLoaded] = useState(false)

  const go = useCallback(
    (delta: number) => {
      const next = index + delta

      if (next < 0 || next >= assets.length) return

      setLoaded(false)
      onIndexChange(next)

      // Pedir más antes de tocar el final: con RTT de ~80 ms desde Lima,
      // esperar al último elemento se nota como un tope.
      if (next >= assets.length - 3) onNeedMore()
    },
    [index, assets.length, onIndexChange, onNeedMore],
  )

  const closeRef = useRef(onClose)
  const pushedRef = useRef(false)

  // Sincronizar en un efecto, no durante el render: React puede descartar un
  // render sin llegar a aplicarlo, y la ref se quedaría con un callback que
  // nunca estuvo en pantalla.
  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  /**
   * Cierre "hacia atrás".
   *
   * La X, Escape y el gesto NO llaman a onClose directamente: retroceden en el
   * historial, y es el popstate el que cierra. Así la entrada que empujamos se
   * consume SIEMPRE por un back() real —el del usuario o el nuestro— y nunca
   * queda una entrada colgada que obligue a pulsar atrás dos veces.
   */
  const requestClose = useCallback(() => {
    if (pushedRef.current) {
      window.history.back()
    } else {
      closeRef.current()
    }
  }, [])

  useEffect(() => {
    // El visor entra en el historial.
    //
    // En móvil, «atrás» es EL gesto para cerrar algo a pantalla completa. Sin
    // esta entrada, atrás sacaba al cliente de la galería entera — y como la
    // sesión se perdía, acababa en la pantalla de contraseña sin entender por
    // qué. Ahora atrás sólo cierra el visor.
    //
    // La guarda evita el doble empujón del montaje doble que React hace en
    // desarrollo: sin ella harían falta dos "atrás" para cerrar.
    if (!pushedRef.current) {
      window.history.pushState({ chasquitamboViewer: true }, '')
      pushedRef.current = true
    }

    const onPop = () => {
      pushedRef.current = false
      closeRef.current()
    }

    window.addEventListener('popstate', onPop)

    // Bloquear el scroll del fondo: sin esto, en iOS el gesto arrastra la
    // galería por debajo del visor y se pierde el sitio.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('popstate', onPop)
      document.body.style.overflow = previous
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
      if (event.key === 'ArrowRight') go(1)
      if (event.key === 'ArrowLeft') go(-1)
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [go, requestClose])

  if (!asset) return null

  const selectBlocked = quotaReached && !asset.selected
  const KindIcon = KIND_ICON[asset.kind]

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={asset.filename}
      className="fixed inset-0 z-50 flex flex-col bg-black/95"
      onTouchStart={(e) => {
        const t = e.touches[0]
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null
      }}
      onTouchEnd={(e) => {
        const start = touchStart.current
        const end = e.changedTouches[0]
        if (!start || !end) return

        const dx = end.clientX - start.x
        const dy = end.clientY - start.y

        // Umbral de 60 px y gesto dominante: sin comparar |dx| con |dy|, un
        // scroll vertical algo torcido cambiaría de foto sin querer.
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
          go(dx < 0 ? 1 : -1)
        } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) {
          // Deslizar hacia abajo cierra: el gesto que ya espera el usuario.
          requestClose()
        }
      }}
    >
      {/*
        Todo lo de dentro va sobre la foto, no sobre la superficie del tema.
        Por eso aquí sí hay blancos y negros fijos: el fondo es la imagen, y una
        imagen puede ser de cualquier color. Los dos colores de marca que
        aparecen usan la variante `-media`, ajustada contra este fondo oscuro.
      */}
      <header className="flex items-center justify-between gap-3 px-4 py-3 pt-safe-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-body">{asset.filename}</p>
          <p className="text-caption text-white/55" data-numeric>
            {index + 1} de {assets.length} · {formatBytes(asset.size_bytes)}
          </p>
        </div>

        <button
          onClick={requestClose}
          aria-label="Cerrar"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        >
          <X size={20} strokeWidth={2} aria-hidden />
        </button>
      </header>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2">
        {asset.preview_url !== null ? (
          <>
            {!loaded && (
              // Un pulso tenue en vez de la palabra «Cargando…»: la vista previa
              // pesa ~200 KB y con 80 ms de latencia el texto alcanza a parpadear
              // justo lo suficiente para molestar.
              <span
                aria-hidden
                className="absolute h-24 w-24 animate-pulse rounded-full bg-white/10"
              />
            )}
            <img
              key={asset.id}
              src={asset.preview_url}
              alt={asset.filename}
              onLoad={() => setLoaded(true)}
              // object-contain, no cover: aquí la foto se ve ENTERA. Es la
              // diferencia entre poder juzgar el encuadre y no poder.
              className={cx(
                'max-h-full max-w-full object-contain transition-opacity duration-200',
                loaded ? 'opacity-100' : 'opacity-0',
              )}
            />
          </>
        ) : (
          <div className="px-6 text-center text-white/70">
            <KindIcon size={48} strokeWidth={1.25} className="mx-auto" aria-hidden />
            <p className="mt-4 text-body">No hay vista previa de este archivo</p>
            {canDownload && (
              <p className="mt-1 text-caption text-white/45">Puedes descargarlo abajo</p>
            )}
          </div>
        )}

        {/* Flechas sólo en escritorio: en móvil se navega deslizando, y unos
            botones flotantes taparían la foto. */}
        {index > 0 && (
          <button
            onClick={() => go(-1)}
            aria-label="Anterior"
            className="absolute left-2 hidden h-12 w-12 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:grid"
          >
            <ChevronLeft size={24} strokeWidth={2} aria-hidden />
          </button>
        )}
        {index < assets.length - 1 && (
          <button
            onClick={() => go(1)}
            aria-label="Siguiente"
            className="absolute right-2 hidden h-12 w-12 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:grid"
          >
            <ChevronRight size={24} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      <footer className="flex items-center justify-center gap-3 px-4 py-4 pb-safe-4">
        {canSelect && (
          <button
            onClick={() => onToggleSelect(asset)}
            disabled={selectBlocked}
            aria-pressed={asset.selected}
            className={cx(
              'flex min-h-12 items-center gap-2 rounded-full px-5 text-body font-medium transition-colors',
              asset.selected
                ? 'bg-select-media text-select-media-contrast'
                : selectBlocked
                  ? 'bg-white/10 text-white/35'
                  : 'bg-white/15 text-white hover:bg-white/25',
            )}
          >
            <Star
              size={18}
              strokeWidth={2}
              fill={asset.selected ? 'currentColor' : 'none'}
              aria-hidden
            />
            {asset.selected ? 'Elegida' : selectBlocked ? 'Cupo completo' : 'Elegir'}
          </button>
        )}

        {canDownload && (
          <button
            onClick={() => onDownload(asset)}
            className="flex min-h-12 items-center gap-2 rounded-full bg-white px-5 text-body font-medium text-black transition-opacity hover:opacity-90"
          >
            <Download size={18} strokeWidth={2} aria-hidden />
            Descargar
          </button>
        )}
      </footer>
    </div>
  )
}
